import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Users, UserPlus, Search, Trash2, ToggleLeft, ToggleRight, X, Eye } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";
import { toast } from "sonner";

export default function ManageUsers({ userRole = "customer" }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: userRole === "agent" ? "Agent@123" : "Customer@123" });
  const [saving, setSaving] = useState(false);
  const [staffForm, setStaffForm] = useState({ name: "", phone: "" });
  const [newStaffCreds, setNewStaffCreds] = useState(null); // { name, phone, temp_password } shown once after creation
  const debounceRef = useRef(null);
  const [group, setGroup] = useState(""); // "" | "retail" | "enterprise" (customer list only)
  const [groupCounts, setGroupCounts] = useState({ retail: null, enterprise: null });

  useEffect(() => { fetchUsers(); }, [userRole]);

  useEffect(() => { if (userRole === "customer") fetchUsers(search); }, [group]);

  useEffect(() => {
    if (userRole !== "customer") return;
    (async () => {
      const out = {};
      for (const t of ["retail", "enterprise"]) {
        try {
          const r = await api.get("/v2/customers/profiles", { params: { page: 1, page_size: 1, customer_type: t } });
          out[t] = r.data?.total ?? null;
        } catch { out[t] = null; }
      }
      setGroupCounts(out);
    })();
  }, [userRole]);

  // Debounced search on every keystroke (customer role uses v2 profile search)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchUsers(search);
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [search]);

  const fetchUsers = async (q = "") => {
    setLoading(true);
    try {
      if (userRole === "customer") {
        // Use v2 customer-profiles search
        const params = {};
        if (q) params.search = q;
        if (group) params.customer_type = group;
        const res = await api.get("/v2/customers/profiles", { params });
        const items = (res.data?.items || []).map((p) => ({
          id: p.customer_id || p.id || p._id,
          name: p.contact_person || p.company_name,
          email: p.email,
          phone: p.mobile,
          wallet_balance: p.wallet_balance || 0,
          customer_type: p.customer_type || "RETAIL",
          is_active: p.is_active !== false,
          created_at: p.created_at,
        }));
        setUsers(items);
      } else if (userRole === "staff") {
        const params = { role: "staff" };
        if (q) params.search = q;
        const res = await api.get("/users", { params });
        setUsers(res.data || []);
      } else {
        const params = { role: userRole };
        if (q) params.search = q;
        const res = await api.get("/users", { params });
        setUsers(res.data || []);
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const clearSearch = () => {
    setSearch("");
    fetchUsers("");
  };

  const toggleActive = async (user) => {
    try {
      await api.put(`/users/${user.id}`, { is_active: !user.is_active });
      toast.success(`User ${user.is_active ? "disabled" : "enabled"}`);
      fetchUsers(search);
    } catch {
      toast.error("Failed to update user status.");
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Deactivate ${user.name}? They will no longer appear in the customer list.`)) return;
    try {
      await api.put(`/v2/customers/profile/${user.id}/deactivate`);
      toast.success("Customer deactivated.");
      fetchUsers(search);
    } catch {
      toast.error("Failed to deactivate customer.");
    }
  };
  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", { ...form, role: userRole });
      toast.success(`${userRole === "agent" ? "Agent" : "Customer"} created!`);
      setShowAdd(false);
      setForm({ name: "", email: "", phone: "", password: userRole === "agent" ? "Agent@123" : "Customer@123" });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create user.");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateStaff = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post("/auth/admin/create-staff", staffForm);
      setNewStaffCreds(res.data);
      setStaffForm({ name: "", phone: "" });
      setShowAdd(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create staff account.");
    } finally {
      setSaving(false);
    }
  };

  const title = userRole === "agent" ? "Agents" : userRole === "staff" ? "Staff" : "Customers";

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">Manage {title}</h1>
            <p className="text-slate-500 text-sm">{users.length} {title.toLowerCase()} registered</p>
          </div>
          <button onClick={() => (userRole === "customer" ? navigate("/admin/customers/new") : setShowAdd(true))} data-testid={`add-${userRole}-btn`}
            className="flex items-center gap-2 px-4 py-2.5 bg-brand-500 text-white text-sm font-semibold rounded-sm hover:bg-brand-600 uppercase">
            <UserPlus className="w-4 h-4" /> Add {userRole === "agent" ? "Agent" : userRole === "staff" ? "Staff" : "Customer"}
          </button>
        </div>

        {userRole === "customer" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="customer-group-cards">
            {[
              { key: "retail", title: "B2C \u2014 Retail", sub: "Individuals. One invoice per shipment; GST filed as one daily summary.", tone: "border-slate-300 bg-slate-50" },
              { key: "enterprise", title: "B2B \u2014 Enterprise, Business, Company", sub: "GST-registered. Invoice-level GST (CGST+SGST or IGST) per customer.", tone: "border-blue-300 bg-blue-50" },
            ].map((g) => (
              <button key={g.key} type="button" data-testid={`group-card-${g.key}`}
                onClick={() => setGroup(group === g.key ? "" : g.key)}
                className={`text-left rounded-lg border p-4 transition-all hover:shadow-sm ${g.tone} ${group === g.key ? "ring-2 ring-brand-500" : ""}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-900">{g.title}</span>
                  <span className="text-2xl font-bold text-slate-900">{groupCounts[g.key] ?? "\u2014"}</span>
                </div>
                <p className="text-xs text-slate-600 mt-1">{g.sub}</p>
                <p className="text-[11px] text-slate-500 mt-1">{group === g.key ? "Showing this group \u2014 click again to clear" : "Click to filter the list"}</p>
              </button>
            ))}
          </div>
        )}

        <form onSubmit={(e) => { e.preventDefault(); fetchUsers(search); }} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${title.toLowerCase()}...`}
              data-testid={`${userRole}-search`}
              className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500" />
          </div>
          <button type="submit" className="px-4 py-2.5 bg-brand-500 text-white text-sm font-semibold rounded-sm">Search</button>
          {search && (
            <button type="button" onClick={clearSearch} data-testid={`${userRole}-clear`}
              className="px-3 py-2.5 border border-slate-200 text-slate-600 text-sm rounded-sm">Clear</button>
          )}
        </form>

        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-400">Loading...</div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center"><Users className="w-12 h-12 text-slate-200 mx-auto mb-3" /><p className="text-slate-500">No {title.toLowerCase()} found.</p></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    {(userRole === "customer"
                      ? ["Name", "Email", "Phone", "Wallet", "Type", "Status", "Joined", "Actions"]
                      : ["Name", "Email", "Phone", "Wallet", "Status", "Joined", "Actions"]
                    ).map((h) => (
                      <th key={h} className="px-3 py-2 text-xs font-bold text-slate-500 uppercase text-left">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {users.map((u) => (
                    <tr key={u.id} data-testid={`${userRole}-row`} className={`hover:bg-slate-50 ${userRole === "customer" ? "cursor-pointer" : ""}`}
                        onClick={(e) => { if (userRole === "customer" && !e.target.closest("button,a,input,select")) navigate(`/admin/customers/${u.id}`); }}>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-brand-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{u.name?.[0]}</div>
                          <span className="font-semibold text-sm text-slate-800">{u.name}</span>{u.customer_code && <span className="ml-2 font-mono text-[11px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded" title="Customer ID">{u.customer_code}</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500">{u.email}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{u.phone || "—"}</td>
                      <td className="px-3 py-2 text-xs font-semibold text-slate-700">₹{(u.wallet_balance || 0).toFixed(2)}</td>
                      {userRole === "customer" && (
                        <td className="px-3 py-2">
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100">
                            {u.customer_type || "RETAIL"}
                          </span>
                        </td>
                      )}
                      <td className="px-3 py-2">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${u.is_active ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}>
                          {u.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-400">{u.created_at ? new Date(u.created_at).toLocaleDateString("en-IN") : "—"}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {userRole === "customer" && (
                            <Link to={`/admin/customers/${u.id}`} data-testid={`view-${u.id}`}
                              className="p-1.5 text-slate-400 hover:text-brand-500 transition-colors rounded">
                              <Eye className="w-4 h-4" />
                            </Link>
                          )}
                          <button onClick={() => toggleActive(u)} data-testid={`toggle-${u.id}`}
                            className="p-1.5 text-slate-400 hover:text-brand-500 transition-colors rounded">
                            {u.is_active ? <ToggleRight className="w-4 h-4 text-emerald-500" /> : <ToggleLeft className="w-4 h-4" />}
                          </button>
                          <button onClick={() => handleDelete(u)} data-testid={`delete-${u.id}`}
                            className="p-1.5 text-slate-400 hover:text-red-500 transition-colors rounded">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showAdd && userRole === "staff" && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl w-full max-w-md p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading font-bold text-lg uppercase">Add Staff</h3>
              <button onClick={() => setShowAdd(false)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <p className="text-xs text-slate-500 mb-4">Staff log in with their phone number. A temporary password will be generated — you'll need to give it to them directly, it's shown only once.</p>
            <form onSubmit={handleCreateStaff} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Full Name</label>
                <input type="text" value={staffForm.name} required
                  onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })}
                  data-testid="add-staff-name"
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Phone Number</label>
                <input type="tel" value={staffForm.phone} required
                  onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })}
                  placeholder="10-digit mobile number"
                  data-testid="add-staff-phone"
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowAdd(false)} className="flex-1 py-2.5 border border-slate-200 text-slate-600 text-sm font-semibold rounded-sm">Cancel</button>
                <button type="submit" disabled={saving} data-testid="add-staff-submit"
                  className="flex-1 py-2.5 bg-brand-500 text-white text-sm font-semibold rounded-sm hover:bg-brand-600 disabled:opacity-70">
                  {saving ? "Creating..." : "Create"}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {showAdd && userRole !== "staff" && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl w-full max-w-md p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading font-bold text-lg uppercase">Add {userRole === "agent" ? "Agent" : "Customer"}</h3>
              <button onClick={() => setShowAdd(false)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              {[
                { name: "name", label: "Full Name", required: true },
                { name: "email", label: "Email", type: "email", required: true },
                { name: "phone", label: "Phone" },
                { name: "password", label: "Password", required: true },
              ].map((f) => (
                <div key={f.name}>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">{f.label}</label>
                  <input type={f.type || "text"} name={f.name} value={form[f.name]} required={f.required}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    data-testid={`add-${userRole}-${f.name}`}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500" />
                </div>
              ))}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowAdd(false)} className="flex-1 py-2.5 border border-slate-200 text-slate-600 text-sm font-semibold rounded-sm">Cancel</button>
                <button type="submit" disabled={saving} data-testid={`add-${userRole}-submit`}
                  className="flex-1 py-2.5 bg-brand-500 text-white text-sm font-semibold rounded-sm hover:bg-brand-600 disabled:opacity-70">
                  {saving ? "Creating..." : "Create"}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
      {newStaffCreds && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl w-full max-w-md p-6 shadow-2xl">
            <h3 className="font-heading font-bold text-lg uppercase text-emerald-600 mb-2">Staff Account Created</h3>
            <p className="text-xs text-slate-500 mb-4">Copy this password now and give it to {newStaffCreds.name} directly. It will not be shown again — they'll be forced to set a new one on first login.</p>
            <div className="space-y-3 mb-5">
              <div className="bg-slate-50 rounded-sm p-3 border border-slate-200">
                <p className="text-[10px] font-bold text-slate-400 uppercase mb-0.5">Login Phone</p>
                <p className="text-sm font-mono font-semibold text-slate-800">{newStaffCreds.phone}</p>
              </div>
              <div className="bg-slate-50 rounded-sm p-3 border border-slate-200">
                <p className="text-[10px] font-bold text-slate-400 uppercase mb-0.5">Temporary Password</p>
                <p className="text-sm font-mono font-semibold text-slate-800" data-testid="new-staff-password">{newStaffCreds.temp_password}</p>
              </div>
            </div>
            <button onClick={() => setNewStaffCreds(null)} data-testid="new-staff-creds-close"
              className="w-full py-2.5 bg-brand-500 text-white text-sm font-semibold rounded-sm hover:bg-brand-600">
              I've Copied It — Close
            </button>
          </motion.div>
        </div>
      )}
    </DashboardLayout>
  );
}
